import { authHeaders, fetchWithAuthRetry, getApiUrl, parseApiError } from './common';
import type { PagedData, PaginationMeta } from './common';

export const codeReviewFindingImpactAreaValues = [
  'UI',
  'BUSINESS_RULE',
  'CONTRACT',
  'DATA',
  'SECURITY',
  'OPS',
  'DOCS',
  'TEST',
  'OTHER',
] as const;

export type CodeReviewFindingImpactArea = (typeof codeReviewFindingImpactAreaValues)[number];

export type CodeReviewImpactAreaCount = {
  impactArea: CodeReviewFindingImpactArea | null;
  findingCount: number;
  p0Count: number;
  p1Count: number;
  p2Count: number;
  p3Count: number;
};

export type CodeReviewFinding = {
  id: string;
  codeReviewId: string;
  severity: 'P0' | 'P1' | 'P2' | 'P3';
  /** 영역 축 도입 이전에 기록된 finding은 null로 내려온다. 화면은 '미분류'로 묶는다. */
  impactArea: CodeReviewFindingImpactArea | null;
  title: string;
  filePath: string;
  lineStart: number | null;
  lineEnd: number | null;
  problem: string;
  impact: string;
  suggestion: string;
  status: string;
  createdPlanId: string | null;
  createdPlan: CodeReviewSourceSummary | null;
  /** 접힌 상태에서도 개수를 보여주기 위한 루트 코멘트 수. 목록의 meta.total과 같은 정의다. */
  commentCount?: number;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
};

export type CodeReviewSourceSummary = {
  id: string;
  title: string;
  webUrl: string | null;
};

export type CodeReviewHistoryMatchStatus = 'MATCHED' | 'NO_MATCH' | 'SKIPPED' | 'FAILED';

export type CodeReviewRelatedHistory = {
  entityType: 'PLAN' | 'COMPLETION_REPORT' | 'POST_MORTEM';
  id: string;
  title: string;
  status: string;
  qualityScore: number | null;
  repositoryId: string | null;
  matchedKeywords: string[];
  matchScore: number;
  createdAt: string;
  webUrl: string | null;
};

export type CodeReviewChangeSetItem = {
  id: string;
  changeSetId: string;
  repositoryId: string | null;
  branchName: string | null;
  targetUrl: string | null;
  mergeOrder: number;
  codeReviewId: string | null;
  note: string | null;
  repository: {
    id: string;
    name: string;
    remoteUrl: string | null;
  } | null;
  codeReview: {
    id: string;
    title: string;
    status: string;
    openP0Count: number;
    openP1Count: number;
    webUrl: string | null;
  } | null;
};

export type CodeReviewChangeSet = {
  id: string;
  projectId: string;
  title: string;
  description: string | null;
  status: 'OPEN' | 'COMPLETED' | 'CANCELLED';
  items: CodeReviewChangeSetItem[];
  primaryReviewWebUrl: string | null;
};

export type CodeReview = {
  id: string;
  projectId: string;
  repositoryId?: string | null;
  title: string;
  status: string;
  targetType: string;
  targetRef: string | null;
  sourcePlanId: string | null;
  sourcePlan: CodeReviewSourceSummary | null;
  sourceCompletionReportId: string | null;
  sourceCompletionReport: CodeReviewSourceSummary | null;
  sourceCommitStart: string | null;
  sourceCommitEnd: string | null;
  sourceBranchName: string | null;
  baseBranchName: string | null;
  diffSummary: string | null;
  testSummary: string | null;
  reviewerContext: string | null;
  runnerType: string | null;
  model: string | null;
  recommendationReason: string | null;
  resultSummary: string | null;
  errorMessage: string | null;
  historyMatchStatus: CodeReviewHistoryMatchStatus;
  relatedHistories?: CodeReviewRelatedHistory[];
  changeSets?: CodeReviewChangeSet[];
  createdByMemberId: string | null;
  createdByAgentId: string | null;
  createdByType: string | null;
  /** 경유 축. 도구 축을 특정하지 못해도 웹 작성물과 에이전트 표면 작성물을 구분한다. */
  createdByCredentialKind: string | null;
  createdByName: string | null;
  createdByAgentName: string | null;
  findingCount: number;
  p0Count: number;
  p1Count: number;
  p2Count: number;
  p3Count: number;
  impactAreaCounts?: CodeReviewImpactAreaCount[];
  findings?: CodeReviewFinding[];
  createdAt: string;
  updatedAt: string;
  completedAt: string | null;
  deletedAt: string | null;
  webUrl: string | null;
};

export const codeReviewTargetTypeValues = [
  'BRANCH_DIFF',
  'GITHUB_PR',
  'GITLAB_MR',
  'LOCAL_DIFF',
  'UPLOADED_DIFF',
  'COMMIT_RANGE',
] as const;

export type CodeReviewTargetType = (typeof codeReviewTargetTypeValues)[number];

type CodeReviewResponse = {
  data: CodeReview;
};

type CodeReviewPagedResponse = {
  data: CodeReview[];
  meta: PaginationMeta;
};

type CodeReviewPlanResponse = {
  data: {
    planId: string;
    webUrl: string;
    findingIds: string[];
  };
};

export type CodeReviewQueryParams = {
  search?: string;
  status?: string;
  targetType?: string;
  dateFrom?: string;
  dateTo?: string;
  severity?: string;
  sourcePlanId?: string;
  sourceCompletionReportId?: string;
  createdByMemberId?: string;
  page?: number;
  pageSize?: number;
};

const buildQuery = (params?: Record<string, string | number | undefined>) => {
  const queryParams = new URLSearchParams();
  for (const [key, value] of Object.entries(params ?? {})) {
    if (value !== undefined && value !== '') queryParams.set(key, String(value));
  }
  return queryParams.toString();
};

export const fetchCodeReviewsPaged = async (
  token: string,
  projectId: string,
  params?: CodeReviewQueryParams,
): Promise<PagedData<CodeReview>> => {
  const query = buildQuery(params);
  const response = await fetchWithAuthRetry(
    `${getApiUrl()}/api/projects/${projectId}/code-reviews${query ? `?${query}` : ''}`,
    {
      cache: 'no-store',
      headers: authHeaders(token),
    },
  );

  if (!response.ok) {
    throw await parseApiError(response, `Failed to fetch code reviews: ${response.status}`);
  }

  return (await response.json()) as CodeReviewPagedResponse;
};

export const fetchCodeReviewById = async (token: string, projectId: string, id: string): Promise<CodeReview> => {
  const response = await fetchWithAuthRetry(`${getApiUrl()}/api/projects/${projectId}/code-reviews/${id}`, {
    cache: 'no-store',
    headers: authHeaders(token),
  });

  if (!response.ok) {
    throw await parseApiError(response, `Failed to fetch code review: ${response.status}`);
  }

  const data = (await response.json()) as CodeReviewResponse;
  return data.data;
};

export type CodeReviewFindingsQueryParams = {
  page?: number;
  pageSize?: number;
};

type CodeReviewFindingsPagedResponse = {
  data: CodeReviewFinding[];
  meta: PaginationMeta;
};

export const fetchCodeReviewFindings = async (
  token: string,
  projectId: string,
  codeReviewId: string,
  params: CodeReviewFindingsQueryParams = {},
): Promise<PagedData<CodeReviewFinding>> => {
  const query = buildQuery(params);
  const response = await fetchWithAuthRetry(
    `${getApiUrl()}/api/projects/${projectId}/code-reviews/${codeReviewId}/findings${query ? `?${query}` : ''}`,
    {
      cache: 'no-store',
      headers: authHeaders(token),
    },
  );

  if (!response.ok) {
    throw await parseApiError(response, `Failed to fetch code review findings: ${response.status}`);
  }

  return (await response.json()) as CodeReviewFindingsPagedResponse;
};

export const dismissCodeReviewFinding = async (
  token: string,
  projectId: string,
  codeReviewId: string,
  findingId: string,
): Promise<CodeReview> => {
  const response = await fetchWithAuthRetry(
    `${getApiUrl()}/api/projects/${projectId}/code-reviews/${codeReviewId}/findings/${findingId}/dismiss`,
    {
      method: 'POST',
      // 본문 없는 POST다. Content-Type: application/json을 붙이면 Fastify가 빈 본문을
      // FST_ERR_CTP_EMPTY_JSON_BODY(400)로 거부하므로 JSON content-type을 보내지 않는다.
      headers: authHeaders(token),
    },
  );

  if (!response.ok) {
    throw await parseApiError(response, `Failed to dismiss finding: ${response.status}`);
  }

  const data = (await response.json()) as CodeReviewResponse;
  return data.data;
};

export const resolveCodeReviewFinding = async (
  token: string,
  projectId: string,
  codeReviewId: string,
  findingId: string,
): Promise<CodeReview> => {
  const response = await fetchWithAuthRetry(
    `${getApiUrl()}/api/projects/${projectId}/code-reviews/${codeReviewId}/findings/${findingId}/resolve`,
    {
      method: 'POST',
      // 본문 없는 POST다. Content-Type: application/json을 붙이면 Fastify가 빈 본문을
      // FST_ERR_CTP_EMPTY_JSON_BODY(400)로 거부하므로 JSON content-type을 보내지 않는다.
      headers: authHeaders(token),
    },
  );

  if (!response.ok) {
    throw await parseApiError(response, `Failed to resolve finding: ${response.status}`);
  }

  const data = (await response.json()) as CodeReviewResponse;
  return data.data;
};

export const undismissCodeReviewFinding = async (
  token: string,
  projectId: string,
  codeReviewId: string,
  findingId: string,
): Promise<CodeReview> => {
  const response = await fetchWithAuthRetry(
    `${getApiUrl()}/api/projects/${projectId}/code-reviews/${codeReviewId}/findings/${findingId}/undismiss`,
    {
      method: 'POST',
      // 본문 없는 POST다. Content-Type: application/json을 붙이면 Fastify가 빈 본문을
      // FST_ERR_CTP_EMPTY_JSON_BODY(400)로 거부하므로 JSON content-type을 보내지 않는다.
      headers: authHeaders(token),
    },
  );

  if (!response.ok) {
    throw await parseApiError(response, `Failed to undismiss finding: ${response.status}`);
  }

  const data = (await response.json()) as CodeReviewResponse;
  return data.data;
};

// 보고서 "리뷰 필요"에서 호출하는 러프한 생성 페이로드.
// title + 출처 보고서 링크만 보내면 서버가 PENDING으로 만들고 targetType을 보고서 컨텍스트로 파생한다.
// 생성 즉시 보고서 reviewStatus가 REQUIRED → IN_REVIEW로 동기화된다.
export const createCodeReview = async (
  token: string,
  projectId: string,
  input: {
    title: string;
    sourceCompletionReportId: string;
    sourcePlanId?: string;
  },
): Promise<CodeReview> => {
  const response = await fetchWithAuthRetry(`${getApiUrl()}/api/projects/${projectId}/code-reviews`, {
    method: 'POST',
    headers: authHeaders(token, true),
    body: JSON.stringify(input),
  });

  if (!response.ok) {
    throw await parseApiError(response, `Failed to create code review: ${response.status}`);
  }

  const data = (await response.json()) as CodeReviewResponse;
  return data.data;
};

export const cancelCodeReview = async (token: string, projectId: string, id: string): Promise<CodeReview> => {
  const response = await fetchWithAuthRetry(`${getApiUrl()}/api/projects/${projectId}/code-reviews/${id}/cancel`, {
    method: 'POST',
    // 본문 없는 POST다. Content-Type: application/json을 붙이면 Fastify가 빈 본문을
    // FST_ERR_CTP_EMPTY_JSON_BODY(400)로 거부하므로 JSON content-type을 보내지 않는다.
    headers: authHeaders(token),
  });

  if (!response.ok) {
    throw await parseApiError(response, `Failed to cancel code review: ${response.status}`);
  }

  const data = (await response.json()) as CodeReviewResponse;
  return data.data;
};

export const deleteCodeReview = async (token: string, projectId: string, id: string): Promise<void> => {
  const response = await fetchWithAuthRetry(`${getApiUrl()}/api/projects/${projectId}/code-reviews/${id}`, {
    method: 'DELETE',
    headers: authHeaders(token),
  });

  if (!response.ok) {
    throw await parseApiError(response, `Failed to delete code review: ${response.status}`);
  }
};

export const createPlanFromCodeReviewFindings = async (
  token: string,
  projectId: string,
  codeReviewId: string,
  input: {
    title: string;
    findingIds: string[];
    priority?: string;
    type?: string;
  },
): Promise<CodeReviewPlanResponse['data']> => {
  const response = await fetchWithAuthRetry(
    `${getApiUrl()}/api/projects/${projectId}/code-reviews/${codeReviewId}/plans`,
    {
      method: 'POST',
      headers: authHeaders(token, true),
      body: JSON.stringify(input),
    },
  );

  if (!response.ok) {
    throw await parseApiError(response, `Failed to create plan from code review findings: ${response.status}`);
  }

  const data = (await response.json()) as CodeReviewPlanResponse;
  return data.data;
};

export type CodeReviewRiskAssessment = {
  level: 'LOW' | 'MEDIUM' | 'HIGH' | 'UNKNOWN';
  intensity: 'STANDARD' | 'FOCUSED' | 'DEEP' | 'CLARIFY';
  reasons: string[];
  ruleVersion: string;
  evaluatedAt: string;
};

export type CodeReviewRecommendation = {
  recommended: boolean;
  reasons: string[];
  sourceCompletionReportId: string;
  suppressedReason?: string | null;
  riskAssessment?: CodeReviewRiskAssessment;
};

export const fetchCodeReviewRecommendation = async (
  token: string,
  projectId: string,
  reportId: string,
): Promise<CodeReviewRecommendation> => {
  const response = await fetchWithAuthRetry(
    `${getApiUrl()}/api/projects/${projectId}/completion-reports/${encodeURIComponent(reportId)}/code-review-recommendation`,
    { cache: 'no-store', headers: authHeaders(token) },
  );
  if (!response.ok) throw await parseApiError(response, `Failed to fetch review recommendation: ${response.status}`);
  const body = (await response.json()) as { data: CodeReviewRecommendation };
  return body.data;
};
